import { DISPOSABLE_DOMAIN_LIST } from "./disposable-email-domains.generated";

/**
 * 一次性邮箱识别。用于注册拦截和一次性免费额度发放两处——两边必须用同一份
 * 判据，否则会出现「注册挡住了但老号建行时还能领额度」这种半拉子状态。
 *
 * 数据来自 2026-08 对生产库的排查：380 个薅额度的账号散在 276 个域名上，
 * 一号一域名，所以按完整域名做黑名单没有意义。真正的抓手是三个：
 *   1. 免费二级域名服务商的父域（一个父域下能无限开子域，那才是闸口）
 *   2. 机器生成的用户名格式（这两种格式在主流邮箱里一个都没出现过）
 *   3. 「10 位随机用户名 + 非主流域名」的组合（见 MAINSTREAM_* 上方注释）
 *   4. 第三方社区维护的一次性邮箱域名名单（见 getThirdPartyDomains）
 *
 * 误伤检查：全部付费用户只用 gmail.com / qq.com / 163.com，与下面的名单
 * 零重叠。
 */

/**
 * 免费/一次性二级域名服务商的父域。匹配方式是「等于它，或者以 .它 结尾」，
 * 所以 youmie.dpdns.org、lsp.lsa1230.dpdns.org 都会被 dpdns.org 覆盖。
 */
const DISPOSABLE_DOMAIN_SUFFIXES = [
	// 仍在活动的（2026-08 排查时近 7 天还在消耗额度）
	"eu.cc",
	"de5.net",
	"cc.cd",
	"ccwu.cc",
	"qzz.io",
	"dpdns.org",
	"us.ci",
	"eu.org",
	"ee.cd",
	"vps.cd",
	"jesys.net",
	"sdfe.app",
	"indevs.in",
	"nsmjj.tech",
	"nsmjj.cc.cd",
	"sfanrj.cfd",
	"hyper.ci",
	"evv.me",
	"my.uy",
	"dickdns.org",
	"localhost.cc",
	"spkun.org",
	"hexname.com",
	"loves-beer.com",
	"lacdn.cn",
	"uron.de5.net",
	"uron.cc.cd",
	"vactor1.us.ci",
	"nothing688.de5.net",
	"nothing688.ccwu.cc",
	"syrf109475.top",
	"lsa1230.dpdns.org",
	// 2026-08-15 补：这批号的用户名是纯字母，下面那条 10 位规则要求含数字，
	// 抓不到它们，所以还是得按域名列。都是五字母 .com 这种批量注册的壳域名。
	"necub.com",
	"oqqaj.com",
	"xghff.com",
	"suarj.com",
	"jgkcr.com",
	"psovv.com",
	"voewo.com",
	"shanyouem.xyz",
	"xuicf1r.site",
	// 2026-09-03 补：对着生产库近 7 天的漏网注册按「可注册父域」聚合抓出来的
	// 一批。前 6 个是通配子域——一个号一个随机子域（oo.rainsase.com、
	// enk.rainsase.com…），按完整域名列永远追不上，只能列父域，后缀匹配覆盖。
	// 后 3 个是平域名聚集：2925.com 的用户名是连号（123456yzy10…16，间隔几
	// 分钟），souss.dev 是 7 位随机串，uqu.me 是 6 位随机串。
	// 排查时这 9 个共 663 个账号、其中 634 个是近 7 天注册的，已领走 5580 点
	// 额度，付费用户 0 个。第三方名单一个都没收。
	"rainsase.com",
	"firegameplay.com",
	"foodhz.com",
	"prominentghost.com",
	"26ai.art",
	"junglemail.org",
	"2925.com",
	"uqu.me",
	"souss.dev",
	// 2026-09-22 补：Xeramail 提供的临时邮箱域名
	"phuturemail.com",
	// 2026-09-22 二次排查（对生产库 4079 个非主流域名账号逐个跑本函数，而不是
	// 按域名抽样——抽样会漏掉「10 位随机用户名」那条组合规则已经覆盖的域名，
	// muzibt.com / itezer.com 就是这么被误判成漏网的）。下面这批是真漏网：
	// - atomicmail.io：76 个号 843 分，其中 70 个是近 7 天注册的，还在加速。
	//   匿名邮箱服务，第三方名单没收。当前敞口里最大的一个。
	// - xeramail.com：phuturemail.com 的母品牌，上一批只加了子域名漏了本体。
	// - darkemail.school：7 个号在 1.2 小时内注册完，脚本特征明显。
	// - uberip.com：4 个号，最后一个 2026-09-21，仍在活动。
	// - okcx.edu.rs / priyo.edu.pl：伪造的 edu 子域，各自 5~9 个号在 1 小时内
	//   建完。列可注册父域，后缀匹配覆盖 bscse./bseee./usa. 这些子域。
	// - pindush.click：同上，通配子域。
	// 刻意没加 miho.uk：8 个号散在 37 天里、用户名长度形态不一，看着像真人，
	// 48 分不值得冒误伤风险。
	"atomicmail.io",
	"xeramail.com",
	"darkemail.school",
	"uberip.com",
	"okcx.edu.rs",
	"priyo.edu.pl",
	"pindush.click",
	// 2026-10-07 补：hidesit.net，9/22 起 200 个号、付费 0 个，用户名是
	// 「形容词.动物.4 字母」，几分钟注册一个。每个号刷 Grok 到出一条就扔，被拒
	// 率 89%，9/23 以来 kie 那 13 次全站 Grok 锁 1 小时里 8 次是它触发的。
	"hidesit.net",
	// 历史大户（当时消耗最高的几个，已停但域名还在）
	"hidepost.net",
	"drmail.in",
	"ruutukf.com",
	"bwmyga.com",
	"voidpop.win",
	"ozsaip.com",
	"dyleris.com",
	"bramble.mom",
	"yzcalo.com",
	"bunmail.one",
	"wnbaldwy.com",
	"bltiwd.com",
	"fastinbox.one",
	"lnovic.com",
	"imgfx.art",
	"flowmail.site",
	"duckmail.sbs",
];

/**
 * 机器生成的用户名格式。这两种在 gmail/qq/163 等主流邮箱里出现次数为 0，
 * 只出现在一次性域名上（近 14 天分别 538 个和 769 个），所以单独成条——
 * 它比域名黑名单稳，对方换域名不换脚本。
 */
const MACHINE_LOCAL_PART_PATTERNS = [
	/^u[0-9a-f]{14}$/, // u + 14 位十六进制
	/^img[a-z0-9]{10}$/, // img + 10 位随机
];

/**
 * 批量注册的域名格式。同一个人一次性注册一批域名、一域名开一个号，按完整
 * 域名列黑名单永远追不上，只能按生成规律匹配。
 */
const DISPOSABLE_DOMAIN_PATTERNS = [
	// ly + 随机的 .cn，排查时一次抓到 20 多个（lyjy85kov.cn、lyrd82asw.cn…）
	/^ly[a-z]{2,3}\d{2}[a-z]{2,4}\.cn$/,
	// nb.syh.* 挂在一堆不同顶级域下（evv.me、hyper.ci、qqc.me、888.moe…）
	/^nb\.syh\./,
];

/**
 * 主流邮箱服务商。两个用途：
 *   1. 下面那条「10 位随机用户名」规则的豁免——那条规则单看用户名的话误伤极高
 *      （qq.com 的用户名就是 10 位 QQ 号，光这一项就有 1000+ 个真人命中），所以
 *      必须先把主流域名摘出去，剩下的自建/野域名才适用。
 *   2. 免费额度白名单（2026-10-11 起，见 isMainstreamEmail）：不在这里的域名照常
 *      注册、付费，只是不送免费额度。按用户名格式或域名拉黑是打地鼠——对方改个
 *      用户名长度、换个临时域名就又漏了；反过来只认大厂邮箱，临时邮箱永远进不来。
 *      当时 30 天里非主流域名的免费用户 1,006 人用掉 12,449 分，付费用户 125 人
 *      里非主流只有 3 个（都已单独列在下面）。
 *
 * 刻意不收：隐私转发（mozmail.com、duck.com、passmail.net、passinbox.com、
 * aleeas.com、bravealias.com）——一个人能无限生成地址，等于临时邮箱；学校域名
 * 不按 .edu 后缀整体放行——okcx.edu.rs、priyo.edu.pl 这类伪 edu 正是薅号来源。
 */
const MAINSTREAM_DOMAINS = new Set([
	"gmail.com",
	"googlemail.com",
	"qq.com",
	"foxmail.com",
	"163.com",
	"126.com",
	"yeah.net",
	"139.com",
	"189.cn",
	"sina.com",
	"sina.cn",
	"sohu.com",
	"aliyun.com",
	"icloud.com",
	"me.com",
	"mac.com",
	"aol.com",
	"msn.com",
	"proton.me",
	"protonmail.com",
	"pm.me",
	"tutanota.com",
	"tuta.io",
	"hushmail.com",
	"yandex.com",
	"yandex.ru",
	"mail.ru",
	"naver.com",
	"daum.net",
	"gmx.com",
	"gmx.de",
	"gmx.net",
	"gmx.at",
	"web.de",
	"t-online.de",
	"zoho.com",
	"zohomail.com",
	"fastmail.com",
	"mail.com",
	"email.com",
	"seznam.cz",
	"wp.pl",
	"o2.pl",
	"interia.pl",
	"libero.it",
	"virgilio.it",
	"orange.fr",
	"free.fr",
	"laposte.net",
	"sfr.fr",
	"uol.com.br",
	"bol.com.br",
	"terra.com.br",
	"rediffmail.com",
	"sapo.pt",
	"abv.bg",
	// 2026-10-11 补：运营商和地区老牌邮箱，生产库里都有真人在用（sky.com 有 1 个
	// 付费用户）。只收见过的，没见过的不预先列。
	"sky.com",
	"btinternet.com",
	"comcast.net",
	"sbcglobal.net",
	"att.net",
	"windstream.net",
	"rogers.com",
	"videotron.ca",
	"bigpond.com",
	"xtra.co.nz",
	"ziggo.nl",
	"planet.nl",
	"telenet.be",
	"bluewin.ch",
	"alice.it",
	"freenet.de",
	"online.de",
	"nifty.com",
	"softbank.ne.jp",
	"op.pl",
	"ukr.net",
	"bk.ru", // bk.ru / inbox.ru / list.ru 是 mail.ru 的别名域
	"inbox.ru",
	"list.ru",
	"tutamail.com", // Tutanota，同 tutanota.com / tuta.io
	"petalmail.com", // 华为 Petal Mail
	// 2026-10-11 补：Waffo 里打开过结账的买家用的自建/机构域名（前三个付成过）。
	// 打开付款页本身就说明是真人，且每个域名只有 1~4 个号，精确匹配没有外溢。
	"manorlakesp12.vic.edu.au",
	"newpelican.com",
	"maxxspace.com",
	"schultz-hohenbrunn.de",
	// 机构自建的 Outlook/Exchange 域名。品牌名不在二级位置，MAINSTREAM_BRAND_DOMAIN
	// 匹配不到，只能精确列。加新条目前先确认是真机构域名——这里放宽一个就等于
	// 送一张批量注册的豁免票。
	"live.saisd.net", // 圣安东尼奥学区，生产库里有真实在用的学生账号
]);

/**
 * outlook / yahoo / live 有一堆国别变体（outlook.jp、yahoo.co.jp、ymail.ne.jp…），
 * 挨个列全不现实，但也不能按前缀放行：`domain.startsWith("yahoo.")` 会把攻击者
 * 自己的 yahoo.attacker.com 当成雅虎，白送一张豁免票——而豁免正是用来挡批量注册
 * 的。生产库里已经有 live.saisd.net（某学区域名）这种前缀撞车的真实例子。
 *
 * 所以要求品牌名必须**恰好是可注册域的二级部分**：后面只允许接公共后缀，
 * 不允许再套私有子域。于是 yahoo.co.jp / outlook.jp / ymail.ne.jp 都放行，
 * 而 yahoo.attacker.com、live.saisd.net 都不放行。
 */
const MAINSTREAM_BRAND_DOMAIN =
	/^(outlook|hotmail|yahoo|live|ymail|rocketmail)\.(com|net|org|co\.[a-z]{2}|com\.[a-z]{2}|ne\.jp|[a-z]{2})$/;

const isMainstreamDomain = (domain: string) =>
	MAINSTREAM_DOMAINS.has(domain) || MAINSTREAM_BRAND_DOMAIN.test(domain);

/** 免费额度白名单判定，见 MAINSTREAM_DOMAINS 上方注释。 */
export function isMainstreamEmail(email: string): boolean {
	return isMainstreamDomain(
		email.slice(email.lastIndexOf("@") + 1).toLowerCase(),
	);
}

/**
 * 10 位小写字母数字、且至少含 1 个数字的用户名（lejifej165、vacafoj164、
 * 76jklbgu0e…）。这是对方注册脚本的固定格式，比域名黑名单稳——他们天天换
 * 域名，但不换脚本。2026-08-15 排查时，光这一条就覆盖了当天 145 个漏网号里
 * 的绝大部分。
 *
 * 「至少含 1 个数字」是刻意加的：不加的话会连纯字母用户名一起吃掉，而
 * aquamaster@seznam.cz、chaiseless@haanhwedding.com 这种真人恰好就长这样。
 * 加上之后拦截量只从 950 掉到 896，换掉了最明显的一类误伤，划算。
 */
const RANDOM_LOCAL_PART = /^(?=.*[0-9])[a-z0-9]{10}$/;

/**
 * 第三方社区名单（7.5 万条精确域名）。跟上面手写的规则是互补，不是替代：
 * 2026-09-02 比对时，手写黑名单 58 条里只有 16 条在这份名单中——免费二级
 * 域名服务商（dpdns.org、eu.org、de5.net）它基本不收，而那批是本站的主力
 * 攻击面。反过来，它覆盖了大量本站还没遇到过的通用临时邮箱服务。
 *
 * 懒加载：模块顶层建 Set 的话，任何 import 过 @repo/utils 的 Lambda 实例都
 * 要付这笔钱，哪怕它这辈子不查邮箱。放到首次调用，只有真正走到判定的实例才建。
 *
 * 实测（Node 22，7.5 万条）：字符串字面量解析 4.9ms + split 建 Set 12.9ms，
 * 每进程一次；之后单次 has() 约 11ns，常驻约 10MB。调用方 initializeUserCredits
 * 只在用户第一次建 UserCredit 行时走一次，11ns 可以忽略。
 */
let thirdPartyDomains: Set<string> | null = null;

const getThirdPartyDomains = () => {
	if (thirdPartyDomains === null) {
		thirdPartyDomains = new Set(DISPOSABLE_DOMAIN_LIST.split("\n"));
	}
	return thirdPartyDomains;
};

export function isDisposableEmail(email: string): boolean {
	const at = email.lastIndexOf("@");
	if (at < 0) {
		return false;
	}

	const localPart = email.slice(0, at).toLowerCase();
	if (MACHINE_LOCAL_PART_PATTERNS.some((re) => re.test(localPart))) {
		return true;
	}

	const domain = email.slice(at + 1).toLowerCase();
	if (DISPOSABLE_DOMAIN_PATTERNS.some((re) => re.test(domain))) {
		return true;
	}

	if (
		DISPOSABLE_DOMAIN_SUFFIXES.some(
			(suffix) => domain === suffix || domain.endsWith(`.${suffix}`),
		)
	) {
		return true;
	}

	// 第三方名单是精确域名匹配（不做后缀展开：它收录的是具体邮箱服务的域名，
	// 按后缀放大会把托管在同一父域下的正经机构一起吃掉）。
	//
	// 主流豁免必须放在它前面：这份数据是外部维护的，我们不控制它的更新，一次
	// 坏提交（比如误收 gmail.com）就等于全站注册关门。豁免兜住这类事故。
	if (!isMainstreamDomain(domain) && getThirdPartyDomains().has(domain)) {
		return true;
	}

	// 组合规则放最后：单看用户名或单看域名都不足以定性，两个条件同时成立才算。
	return RANDOM_LOCAL_PART.test(localPart) && !isMainstreamDomain(domain);
}
